import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, WhatsappLogo, EnvelopeSimple, PaperPlaneTilt } from "@phosphor-icons/react";

const empty = { name: "", email: "", whatsapp: "", role: "Responder", active: true };

export default function CrewPage() {
  const [crew, setCrew] = useState([]);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [twilio, setTwilio] = useState(null);

  const load = () => api.get("/crew").then((r) => setCrew(r.data));

  useEffect(() => {
    load();
    api.get("/settings/twilio").then((r) => setTwilio(r.data)).catch((e) => console.error("twilio settings load failed", e));
  }, []);

  const save = async () => {
    try {
      if (editId) await api.put(`/crew/${editId}`, form);
      else await api.post("/crew", form);
      toast.success(editId ? "Crew member updated" : "Crew member added");
      setForm(null); setEditId(null); load();
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || e.message });
    }
  };

  const del = async (id) => {
    await api.delete(`/crew/${id}`);
    toast.success("Crew member removed");
    load();
  };

  const sendActivation = async (id) => {
    try {
      const r = await api.post(`/crew/${id}/send-activation`);
      toast.success(`Activation email sent to ${r.data.to}`, { description: "One-tap WhatsApp activation link included." });
    } catch (e) {
      toast.error("Activation email failed", { description: e.response?.data?.detail || e.message });
    }
  };

  const testWhatsapp = async (id) => {
    try {
      const r = await api.post(`/crew/${id}/test-whatsapp`);
      if (String(r.data.status).startsWith("sent")) toast.success(`Test WhatsApp sent to ${r.data.to}`, { description: r.data.status });
      else toast.error("WhatsApp test failed", { description: r.data.status });
    } catch (e) {
      toast.error("WhatsApp test failed", { description: e.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="p-8 max-w-5xl" data-testid="crew-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-heading text-4xl font-black tracking-tighter uppercase">Responder Crew</h1>
          <p className="text-zinc-400 text-sm mt-1">Dispatches are forwarded to every active member by email and — once Twilio is configured — pushed to their WhatsApp from your ResQconnect sender.</p>
        </div>
        <button onClick={() => { setForm({ ...empty }); setEditId(null); }} className="btn-tactical bg-[#007AFF] hover:bg-[#005ecb] text-white flex items-center gap-2" data-testid="add-crew-button">
          <Plus size={14} weight="bold" /> Add Member
        </button>
      </div>

      {twilio && !twilio.configured && (
        <div className="border border-yellow-500/30 bg-yellow-500/10 p-3 mt-4 text-xs text-yellow-200" data-testid="crew-twilio-warning">
          WhatsApp alerts are paused — Twilio not configured. <a href="/settings" className="underline font-bold">Set it up in Settings</a>. Email dispatch continues to work.
        </div>
      )}

      {form && (
        <div className="border border-zinc-800 bg-[#121214] p-6 mt-6 grid md:grid-cols-2 gap-4" data-testid="crew-form">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Name</label>
            <input className="tactical-input mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="crew-name-input" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Role</label>
            <input className="tactical-input mt-1" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="crew-role-input" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Forwarding Email</label>
            <input className="tactical-input mt-1 font-mono-data text-xs" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="crew-email-input" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">WhatsApp Number (e.g. +27821234567)</label>
            <input className="tactical-input mt-1 font-mono-data text-xs" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="crew-whatsapp-input" />
          </div>
          <div className="md:col-span-2 border border-green-500/20 bg-green-500/5 p-3 text-xs text-zinc-400" data-testid="twilio-instructions">
            <span className="text-green-400 font-bold">CONNECTING A RESPONDER:</span> save the member, then click the yellow
            <span className="text-yellow-400 font-bold"> paper-plane button</span> on their row — they get a branded email with a one-tap WhatsApp activation link. When they reply <b>ACTIVATE</b> from their phone, the session opens automatically and every dispatch is pushed to their WhatsApp from your ResQconnect sender.
          </div>
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="crew-active-checkbox" />
            Active (receives dispatches)
          </label>
          <div className="flex gap-2 md:col-span-2">
            <button onClick={save} className="btn-tactical bg-[#007AFF] hover:bg-[#005ecb] text-white" data-testid="crew-save-button">Save</button>
            <button onClick={() => { setForm(null); setEditId(null); }} className="btn-tactical bg-zinc-800 hover:bg-zinc-700 text-white">Cancel</button>
          </div>
        </div>
      )}

      <div className="border border-zinc-800 mt-6">
        {crew.map((m) => (
          <div key={m.id} className="flex items-center justify-between p-5 border-b border-zinc-800 last:border-0 bg-[#121214] hover:bg-zinc-900 transition-colors" data-testid={`crew-row-${m.id}`}>
            <div>
              <div className="flex items-center gap-3">
                <span className="font-heading font-bold">{m.name}</span>
                <span className="text-[10px] font-bold px-2 py-0.5 border border-zinc-700 text-zinc-400 rounded-sm uppercase">{m.role}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 border rounded-sm ${m.active ? "bg-green-500/10 text-green-400 border-green-500/20" : "bg-zinc-500/10 text-zinc-500 border-zinc-500/20"}`}>
                  {m.active ? "ACTIVE" : "INACTIVE"}
                </span>
                <span className={`text-[10px] font-bold px-2 py-0.5 border rounded-sm ${m.whatsapp_active ? "bg-green-500/10 text-green-400 border-green-500/20" : "bg-yellow-500/10 text-yellow-400 border-yellow-500/20"}`} data-testid={`crew-wa-status-${m.id}`}>
                  {m.whatsapp_active ? "WA CONNECTED" : "WA PENDING"}
                </span>
              </div>
              <div className="flex items-center gap-4 mt-1.5 font-mono-data text-xs text-zinc-400">
                <span className="flex items-center gap-1"><EnvelopeSimple size={13} /> {m.email}</span>
                <span className="flex items-center gap-1 text-green-400"><WhatsappLogo size={13} /> {m.whatsapp || "—"}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => sendActivation(m.id)} title="Email WhatsApp activation link" className="p-2 border border-zinc-800 hover:bg-zinc-800 rounded-sm text-yellow-400" data-testid={`crew-send-activation-${m.id}`}><PaperPlaneTilt size={16} /></button>
              <button onClick={() => testWhatsapp(m.id)} title="Send test WhatsApp" className="p-2 border border-zinc-800 hover:bg-zinc-800 rounded-sm text-green-400" data-testid={`crew-test-wa-${m.id}`}><WhatsappLogo size={16} /></button>
              <button onClick={() => { setEditId(m.id); setForm({ name: m.name, email: m.email, whatsapp: m.whatsapp || "", role: m.role, active: m.active }); }} className="p-2 border border-zinc-800 hover:bg-zinc-800 rounded-sm" data-testid={`crew-edit-${m.id}`}><PencilSimple size={16} /></button>
              <button onClick={() => del(m.id)} className="p-2 border border-zinc-800 hover:bg-red-500/20 text-red-400 rounded-sm" data-testid={`crew-delete-${m.id}`}><Trash size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
